import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  useGetCompanyNotificationPreferencesQuery,
  useUpdateCompanyNotificationPreferencesMutation,
  useGetCompanyStaffQuery,
} from "../../redux/services/settingsApi";
import { useAppSelector } from "../../redux/services/store";
import { selectUser } from "../../redux/slices/authSlice";
import {
  isSoundEnabled,
  playTestAlarm,
  setSoundEnabled,
  subscribeSoundEnabled,
} from "../../utils/notificationSound";

type NotificationOption = {
  id: "email" | "inApp";
  label: string;
  description: string;
};

// Saved in this browser only (no backend field), so each computer can choose:
// the dispatch desk keeps it on, a laptop used in meetings can turn it off.
function AlertSoundSetting() {
  const [enabled, setEnabled] = useState(isSoundEnabled());
  useEffect(() => subscribeSoundEnabled(setEnabled), []);

  const handleToggle = () => {
    const next = !enabled;
    setSoundEnabled(next);
    toast.success(next ? "Alert sound turned on" : "Alert sound turned off");
  };

  const handleTest = async () => {
    if (!(await playTestAlarm())) {
      toast.error("Your browser blocked the sound. Check that this tab isn't muted, then try again.");
    }
  };

  return (
    <div className="mt-8 border-t pt-6">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">This device</p>
      <div className="flex flex-col gap-3 px-1 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div className="min-w-0 sm:flex-1">
          <p className="font-medium text-gray-900">Alert sound</p>
          <p className="text-sm text-gray-500">
            Ring when day-to-day orders and relocation requests are waiting for you, and when a customer accepts your offer or pays.
            Applies to this browser only. Pop-up alerts still show when it's off.
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center justify-between gap-3 sm:justify-end">
          <button
            type="button"
            onClick={handleTest}
            disabled={!enabled}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Play test sound
          </button>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label="Alert sound"
            onClick={handleToggle}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
              enabled ? "bg-blue-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function NotificationPreferences() {
  return (
    <>
      <AccountNotificationPreferences />
      <AlertSoundSetting />
    </>
  );
}

function AccountNotificationPreferences() {
  const user = useAppSelector(selectUser);
  const userId = (user as any)?.id;

  // fetch staff members linked to this user
  const {
    data: staffData,
    isLoading: isStaffLoading,
  } = useGetCompanyStaffQuery(
    { companyId: userId },
    { skip: !userId }
  );

  // extracted companyId from staffData if available
  const companyId =
    staffData?.data?.staff_members?.[0]?.company_id ?? undefined;

  const {
    data: notificationPrefs,
    isLoading: isPrefsLoading,
    refetch,
  } = useGetCompanyNotificationPreferencesQuery(
    { companyId },
    { skip: !companyId }
  );

  const [updatePrefs, { isLoading: isUpdating }] =
    useUpdateCompanyNotificationPreferencesMutation();

  const options: NotificationOption[] = [
    {
      id: "email",
      label: "Email",
      description: "Receive updates directly to your registered email address.",
    },
    {
      id: "inApp",
      label: "In app",
      description: "View alerts directly within the Fleetrify dashboard.",
    },
  ];

  const [enabled, setEnabled] = useState<Record<string, boolean>>({
    email: false,
    inApp: false,
  });

  const [hasChanges, setHasChanges] = useState(false);

  //load preferences when click on the tab
  useEffect(() => {
    const pref = Array.isArray(notificationPrefs)
      ? notificationPrefs[0]
      : notificationPrefs;

    if (pref) {
      setEnabled({
        email: Boolean(pref.notify_email),
        inApp: Boolean(pref.notify_inapp),
      });
    }
  }, [notificationPrefs]);

  const handleToggle = (id: "email" | "inApp") => {
    setEnabled((prev) => {
      const updated = { ...prev, [id]: !prev[id] };
      setHasChanges(true);
      return updated;
    });
  };

  const handleSave = async () => {
    try {
      await updatePrefs({
        companyId: companyId!,
        data: {
          notify_email: enabled.email,
          notify_inapp: enabled.inApp,
        },
      }).unwrap();
      toast.success("Notification preferences updated");
      setHasChanges(false);
      refetch();
    } catch (error) {
      toast.error("Failed to update preferences");
    }
  };

  if (isStaffLoading || isPrefsLoading) return <p>Loading preferences...</p>;
  if (!companyId) return <p>No company ID found for this user.</p>;

  return (
    <div className="divide-y">
      {options.map((option) => (
        <div
          key={option.id}
          className="flex justify-between items-center px-1 py-4"
        >
          <div>
            <p className="font-medium text-gray-900">{option.label}</p>
            <p className="text-sm text-gray-500">{option.description}</p>
          </div>

          {/* toggle switch */}
          <button
            onClick={() => handleToggle(option.id)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              enabled[option.id] ? "bg-blue-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                enabled[option.id] ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      ))}

      {/* save button */}
      <div className="pt-6">
        <button
          onClick={handleSave}
          disabled={!hasChanges || isUpdating}
          className={`w-[20%] py-2 rounded-md text-white font-medium transition ${
            !hasChanges || isUpdating
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {isUpdating ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

